import * as THREE from 'three';

export interface VRHudState {
  health: number;      // 0..max
  maxHealth: number;
  boostCharge: number;
  progress: number;    // 0..1 track position
  boss: { health: number; maxHealth: number } | null;
}

/**
 * In-headset HUD: the DOM overlay doesn't exist inside WebXR, so vitals are
 * drawn into a small physical dashboard display. It intentionally contains
 * only glanceable bars — mission copy and score belong on modal screens, not
 * across the driver's road view. Redraws only when a visible value changes.
 */
export class VRHud {
  readonly mesh: THREE.Mesh;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture;
  private lastKey = '';

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1024;
    this.canvas.height = 160;
    this.ctx = this.canvas.getContext('2d')!;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.colorSpace = THREE.SRGBColorSpace;

    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    material.depthTest = true;
    material.depthWrite = false;

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.74, 0.116), material);
    this.mesh.rotation.x = -0.12;
    this.mesh.renderOrder = 7;
    this.mesh.visible = false;
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible;
  }

  update(state: VRHudState): void {
    if (!this.mesh.visible) return;

    // only repaint when something the player can see has changed
    const key = [
      Math.round(state.health),
      Math.round(state.boostCharge / 5),
      Math.round(state.progress * 200),
      state.boss ? Math.round(state.boss.health) : -1,
    ].join('|');
    if (key === this.lastKey) return;
    this.lastKey = key;

    const ctx = this.ctx;
    this.paintBackdrop();

    // Two instruments only: condition and boost. The percentage is retained
    // because bars alone are difficult to distinguish in peripheral vision.
    const hp = Math.max(0, state.health / state.maxHealth);
    const healthColor = hp > 0.6 ? '#41d17a' : hp > 0.3 ? '#ffc247' : '#ff5544';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = healthColor;
    ctx.font = "700 34px 'Audiowide', 'Courier New', monospace";
    ctx.fillText(`${Math.ceil(hp * 100)}`, 28, 49);
    this.paintBar(132, 28, 360, 36, hp, healthColor, 10);

    ctx.fillStyle = '#2ee6d6';
    ctx.font = "700 30px 'Audiowide', 'Courier New', monospace";
    ctx.fillText('>>', 535, 49);
    this.paintBar(612, 28, 384, 36, Math.max(0, Math.min(1, state.boostCharge / 100)), '#2ee6d6', 10);

    // The lower strip swaps from route position to target health on contact.
    const barX = 28;
    const barY = 94;
    const barWidth = 968;
    if (state.boss) {
      this.paintBar(barX, barY, barWidth, 34, Math.max(0, state.boss.health / state.boss.maxHealth), '#ff4433', 12);
    } else {
      this.paintBar(barX, barY, barWidth, 24, Math.min(1, state.progress), '#ff2d95', 8);
    }

    this.texture.needsUpdate = true;
  }

  private paintBackdrop(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = 'rgba(4,7,13,0.98)';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.strokeStyle = 'rgba(92,238,244,0.7)';
    ctx.lineWidth = 3;
    ctx.strokeRect(3, 3, this.canvas.width - 6, this.canvas.height - 6);
    const accent = ctx.createLinearGradient(0, 0, this.canvas.width, 0);
    accent.addColorStop(0, '#ff2d95');
    accent.addColorStop(0.48, '#ff2d95');
    accent.addColorStop(0.52, '#2ee6d6');
    accent.addColorStop(1, '#2ee6d6');
    ctx.fillStyle = accent;
    ctx.fillRect(3, 3, this.canvas.width - 6, 6);
  }

  private paintBar(x: number, y: number, width: number, height: number, ratio: number, color: string, segments: number): void {
    const value = Math.max(0, Math.min(1, ratio));
    this.ctx.fillStyle = '#080b12';
    this.ctx.fillRect(x, y, width, height);
    this.ctx.strokeStyle = 'rgba(210,224,240,0.58)';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(x, y, width, height);
    const innerWidth = width - 8;
    const gap = 5;
    const segmentWidth = (innerWidth - gap * (segments - 1)) / segments;
    const lit = Math.ceil(value * segments);
    for (let i = 0; i < lit; i++) {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(x + 4 + i * (segmentWidth + gap), y + 4, segmentWidth, height - 8);
    }
  }
}
